import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { MaskLines, FadeUp } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProjectCard from "../components/ProjectCard";

const HERO_IMG = "https://images.unsplash.com/photo-1724582586458-a51791349977?crop=entropy&cs=srgb&fm=jpg&q=85";
const PORTRAIT_IMG = "https://images.pexels.com/photos/6615203/pexels-photo-6615203.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHAPTERS = [
  { num: "01", title: "Light as Primary Material", body: "Before walls, before furniture — we plan the sun. Every Space Arts drawing begins with where the light enters and how it will move through the day, because light is the one material that never ages." },
  { num: "02", title: "The Subtractive Gesture", body: "A room is finished not when nothing more can be added, but when nothing more can be taken away. We remove, hollow and quiet until a space holds only what earns its place." },
  { num: "03", title: "Eternal Patina", body: "We choose materials that record time — lime plaster, brass, stone and timber that deepen with touch. Our buildings are designed to be more beautiful in ten years than on day one." },
];

const MARQUEE_ITEMS = ["Residential Monoliths", "Spatial Minimalism", "Material Integrity", "Sensory Interiors", "Bespoke Millwork", "Architectural Poetry"];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.07]);

  useEffect(() => {
    api.get("/projects", { params: { featured: "true" } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const portraitSrc = settings?.principal_photo || PORTRAIT_IMG;

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} className="relative px-6 sm:px-10 pt-28 sm:pt-40 pb-10 min-h-screen flex flex-col justify-center overflow-hidden">
        <div className="max-w-[1400px] mx-auto w-full grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">
                <span className="h-px w-10 bg-[#C29B38]" />
                Studio of Architecture & Spatial Poetics
              </p>
            </FadeUp>
            <MaskLines
              as="h1"
              delay={0.15}
              className="mt-7 font-serif text-[13.5vw] sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[1.03] tracking-tight"
              lines={[
                "Crafting Stillness",
                "Between Form",
                { node: <span><em className="italic">& Void</em><span className="text-[#C29B38]">.</span></span> },
              ]}
            />
            <FadeUp delay={0.6}>
              <p className="mt-8 max-w-md font-light text-[#57534E] text-base sm:text-lg leading-relaxed">
                Space Arts by Ak is an architecture & interior design studio shaping residences, cultural spaces and hospitality interiors that age with grace.
              </p>
            </FadeUp>
            <FadeUp delay={0.75} className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/projects"
                data-testid="hero-explore-button"
                className="group inline-flex items-center gap-3 bg-[#181816] text-[#F8F6F0] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#B85D3B] transition-colors duration-300"
              >
                View Projects
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-inquire-button"
                className="group inline-flex items-center gap-3 border border-[#181816]/25 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:border-[#181816] hover:bg-[#181816] hover:text-[#F8F6F0] transition-all duration-300"
              >
                Inquire Studio
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </FadeUp>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div style={{ y: heroY, scale: heroScale }} className="relative">
              <div className="frame-clip img-zoom bg-[#EFECE6]">
                <img
                  src={HERO_IMG}
                  alt="Signature minimalist interior by Space Arts by Ak"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div className="absolute bottom-5 left-5 bg-[#F8F6F0]/90 backdrop-blur px-4 py-3 font-mono text-[10px] tracking-[0.22em] uppercase">
                Fig. 01 — Light Study
              </div>
            </motion.div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto w-full mt-14 hidden sm:flex justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">
          <span>Architecture — Interiors — Objects</span>
          <span>Scroll ↓</span>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee
        items={MARQUEE_ITEMS}
        className="border-y hairline py-6 sm:py-8 bg-[#F1EFEA]"
        itemClassName="text-2xl sm:text-4xl text-[#181816]/60"
      />

      {/* MANIFESTO */}
      <section className="px-6 sm:px-10 py-24 sm:py-32 bg-[#EFECE6]">
        <div className="max-w-[1400px] mx-auto">
          <FadeUp>
            <p className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">
              <span className="h-px w-10 bg-[#B85D3B]" /> Manifesto — How we think
            </p>
          </FadeUp>
          <div className="mt-12">
            {CHAPTERS.map((c) => (
              <FadeUp key={c.num}>
                <div className="grid md:grid-cols-12 gap-4 md:gap-8 py-10 sm:py-14 border-t hairline">
                  <div className="md:col-span-2 font-serif italic text-5xl sm:text-6xl text-[#C29B38]">{c.num}</div>
                  <h3 className="md:col-span-5 font-serif text-2xl sm:text-4xl leading-tight">{c.title}</h3>
                  <p className="md:col-span-5 font-light text-[#57534E] leading-relaxed">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS — bento */}
      <section className="px-6 sm:px-10 py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <FadeUp>
              <p className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">
                <span className="h-px w-10 bg-[#C29B38]" /> Selected Work
              </p>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                Spaces we have <em className="italic">quieted</em>.
              </h2>
            </FadeUp>
            <FadeUp delay={0.15}>
              <Link to="/projects" data-testid="home-all-projects-link" className="link-underline font-mono text-[11px] uppercase tracking-[0.22em] text-[#57534E] hover:text-[#181816] transition-colors">
                All Projects →
              </Link>
            </FadeUp>
          </div>

          <div className="mt-14 grid md:grid-cols-12 gap-x-8 gap-y-16">
            {featured.map((p, i) => (
              <FadeUp key={p.id} className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5 md:mt-24" : i === 2 ? "md:col-span-5" : "md:col-span-7"}>
                <ProjectCard
                  project={p}
                  index={i}
                  aspect={i === 0 ? "aspect-[16/11]" : i === 1 ? "aspect-[4/5]" : "aspect-[4/3]"}
                />
              </FadeUp>
            ))}
            {featured.length === 0 && (
              <p className="md:col-span-12 font-light text-[#8A857D]">Featured projects will appear here once added in the Studio Dashboard.</p>
            )}
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="px-6 sm:px-10 py-24 sm:py-32 bg-[#EFECE6]">
        <div className="max-w-[1400px] mx-auto">
          <FadeUp>
            <p className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">
              <span className="h-px w-10 bg-[#B85D3B]" /> Disciplines
            </p>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">What the studio does.</h2>
          </FadeUp>
          <div className="mt-14">
            {services.map((s, i) => (
              <FadeUp key={s.id}>
                <Link
                  to="/services"
                  data-testid={`home-service-row-${i}`}
                  className="group flex items-baseline justify-between gap-6 py-8 border-t hairline hover:bg-[#F8F6F0]/60 transition-colors duration-300 px-2 sm:px-4 -mx-2 sm:-mx-4"
                >
                  <div className="flex items-baseline gap-6 sm:gap-10">
                    <span className="font-mono text-xs text-[#C29B38]">0{i + 1}</span>
                    <h3 className="font-serif text-2xl sm:text-4xl group-hover:italic transition-all duration-300">{s.title}</h3>
                  </div>
                  <ArrowUpRight className="h-5 w-5 shrink-0 self-center opacity-30 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PRINCIPAL */}
      <section className="px-6 sm:px-10 py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <FadeUp>
              <div className="frame-clip img-zoom bg-[#EFECE6]">
                <img src={portraitSrc} alt="Ak — Principal Architect, Space Arts" className="w-full aspect-[4/5] object-cover" loading="lazy" />
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8A857D]">Fig. 02 — The Principal</p>
            </FadeUp>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <FadeUp>
              <p className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">
                <span className="h-px w-10 bg-[#C29B38]" /> The Principal
              </p>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl tracking-tight">Ak.</h2>
              <blockquote className="mt-8 font-serif italic text-2xl sm:text-3xl leading-snug text-[#181816]">
                “I design the pause between two walls — the client gets the architecture, and the silence gets the credit.”
              </blockquote>
              <p className="mt-8 font-light text-[#57534E] leading-relaxed">
                Ak leads every project personally — from the first sun-path sketch to the last brass detail on site. The studio stays deliberately small so that nothing is drawn by strangers.
              </p>
              <Link to="/about" data-testid="home-about-link" className="link-underline mt-8 inline-block font-mono text-[11px] uppercase tracking-[0.22em] text-[#57534E] hover:text-[#181816] transition-colors">
                More about the studio →
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-[#181816] text-[#F8F6F0] px-6 sm:px-10 py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row justify-between gap-12">
          <FadeUp>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              Have a space
              <br />
              <em className="italic text-[#C29B38]">in mind?</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="max-w-sm md:pt-4">
            <p className="font-light text-[#B8B4AB] leading-relaxed">
              Tell us about the site, the life it should hold, and the stillness you are looking for. We take on a limited number of commissions each year.
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-inquire"
              className="mt-8 inline-flex items-center gap-3 bg-[#F8F6F0] text-[#181816] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#C29B38] hover:text-[#181816] transition-colors duration-300"
            >
              Start an Inquiry <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
